import React, { useState } from 'react'
import { Tooltip, Checkbox } from 'antd'
import {
  CustomModalStyled,
  EduButton,
  FlexContainer,
  MathFormulaDisplay,
} from '@edulastic/common'
import { LANGUAGE_EN } from '@edulastic/constants/const/languages'
import { sanitizeHtml } from '@edulastic/common/src/utils/html'
import { IconUserRegular } from '@edulastic/icons'
import styled from 'styled-components'
import { changeTestDataToPreferredLanguage } from '../assessment/utils/test'
import { isTextToSpeechEnabled } from '../assessment/utils/helpers'
import AudioControls from '../assessment/AudioControls'
import {
  StyledStudentName,
  StyledStudentNameContainer,
  StyledTestName,
} from './styled'
import { redirectToDashbord } from './utils'

const TestInfoModal = ({
  pauseAllowed,
  allowedTime,
  multiLanguageEnabled,
  setSelectedLanguage,
  languagePreference,
  timedAssignment,
  hasInstruction,
  attemptCount,
  maxAttempts,
  startAssignment,
  testId,
  assignmentId,
  testType,
  classId,
  history,
  title,
  notifyCancel,
  closeTestPreviewModal,
  preview,
  hasSections,
  userName,
  safeBrowser,
  lastAttemptId,
  passwordPolicy,
  test,
  visible = true,
  onCancel = () => {},
  // TTS-related props
  showTextToSpeech,
  showUserTTS,
  ttsSubjectMatch = true,
  userRole,
  isTestPreviewModalVisible = false,
  userFeatures,
}) => {
  const selectedLang = multiLanguageEnabled
    ? languagePreference || LANGUAGE_EN
    : LANGUAGE_EN
  const testDataByLanguage = changeTestDataToPreferredLanguage(
    test,
    selectedLang
  )

  const [acknowledgementChecked, setAcknowledgementChecked] = useState(false)

  const {
    instructionAcknowledgementText = '',
    instruction = '',
    showInstructionAcknowledgement,
  } = testDataByLanguage || {}

  const timedContent = pauseAllowed ? (
    <p style={{ margin: '10px 0' }}>
      {' '}
      This is a timed assignment which should be finished within the time limit
      set for this assignment. The time limit for this assignment is{' '}
      <span data-cy="test-time" style={{ fontWeight: 700 }}>
        {' '}
        {allowedTime / (60 * 1000)} minutes
      </span>
      . Do you want to continue?
    </p>
  ) : (
    <p style={{ margin: '10px 0' }}>
      {' '}
      This is a timed assignment which should be finished within the time limit
      set for this assignment. The time limit for this assignment is{' '}
      <span data-cy="test-time" style={{ fontWeight: 700 }}>
        {' '}
        {allowedTime / (60 * 1000)} minutes
      </span>{' '}
      and you can&apos;t quit in between. Do you want to continue?
    </p>
  )

  const handleCancel = () => {
    setSelectedLanguage('')
    if (notifyCancel) {
      redirectToDashbord('HOME', history)
    }
    if (preview) {
      closeTestPreviewModal()
    }
    if (onCancel) {
      onCancel()
    }
  }

  const handleOk = () => {
    if (attemptCount < maxAttempts) {
      startAssignment({
        testId,
        assignmentId,
        testType,
        classId,
        selectedLang,
        hasSections,
        safeBrowser,
        lastAttemptId,
        passwordPolicy,
      })
    }
    if (!preview) {
      onCancel()
    }
    if (preview && multiLanguageEnabled) {
      return !selectedLang
    }
  }

  const audioSrc = testDataByLanguage?.testTTS?.instructionAudioURL

  // Check if TTS is enabled for the user
  const isTtsEnabled = isTextToSpeechEnabled(
    showTextToSpeech,
    showUserTTS,
    isTestPreviewModalVisible,
    userRole,
    userFeatures?.textToSpeech,
    ttsSubjectMatch
  )

  const content = (
    <>
      <FlexContainer
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        padding="0px 0px 16px 0px"
      >
        <Tooltip title={title}>
          <StyledTestNameTestInfoModal>{title}</StyledTestNameTestInfoModal>
        </Tooltip>
        <Tooltip title={userName}>
          <StyledStudentNameContainer>
            <IconUserRegular height="24px" width="24px" />
            <StyledStudentNameTestInfoModal>
              Hi, {userName}!
            </StyledStudentNameTestInfoModal>
          </StyledStudentNameContainer>
        </Tooltip>
      </FlexContainer>
      {audioSrc && isTtsEnabled && (
        <AudioControls
          key={test._id}
          test={testDataByLanguage}
          audioSrc={audioSrc}
          className="speakable-text-audio-controls"
          isTestSettings
          padding="8px 0px"
        />
      )}
      <FlexContainer flexDirection="column" padding=" 16px 0px">
        {timedAssignment && (
          <div>
            <p>TIME LIMIT</p>
            <p>{timedContent}</p>
          </div>
        )}
        {hasInstruction && instruction && (
          <p style={{ margin: '0px' }} data-cy="instructionText">
            <MathFormulaDisplay
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(instruction) }}
            />
          </p>
        )}
        {showInstructionAcknowledgement && instructionAcknowledgementText && (
          <Checkbox
            checked={acknowledgementChecked}
            onChange={(e) => setAcknowledgementChecked(e.target.checked)}
            data-cy="instructionAcknowledgementCheckbox"
          >
            <p
              style={{ display: 'inline' }}
              data-cy="instructionAcknowledgementText"
            >
              {instructionAcknowledgementText}
            </p>
          </Checkbox>
        )}
      </FlexContainer>
      <FlexContainer justifyContent="center" gap="10px" padding="32px 0 0 0">
        <EduButton type="primary" isGhost onClick={handleCancel}>
          NO, CANCEL
        </EduButton>
        <EduButton
          type="primary"
          disabled={
            showInstructionAcknowledgement &&
            instructionAcknowledgementText &&
            !acknowledgementChecked
          }
          onClick={handleOk}
          data-cy="continueTestButton"
        >
          YES, CONTINUE
        </EduButton>
      </FlexContainer>
    </>
  )

  return (
    <CustomModalStyled
      open={visible}
      centered
      maskClosable={!preview}
      visible
      closable={false}
      destroyOnClose
      footer={null}
    >
      {content}
    </CustomModalStyled>
  )
}

export default TestInfoModal

const StyledTestNameTestInfoModal = styled(StyledTestName)`
  font-size: 22px;
  color: #434b5d;
  font-weight: 700;
`
const StyledStudentNameTestInfoModal = styled(StyledStudentName)`
  font-size: 22px;
  max-width: 100%;
`
